import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, ChevronRight, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const NAV_SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "authentication", label: "Authentication" },
  { id: "tenant-config", label: "Tenant Configuration" },
  { id: "capabilities", label: "Capability Management" },
  { id: "prompt-overrides", label: "Prompt Overrides" },
  { id: "test-endpoint", label: "Test Endpoint" },
  { id: "api-reference", label: "API Reference" },
  { id: "data-models", label: "Data Models" },
];

function CodeBlock({ code, language = "http", className = "" }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`relative rounded-xl overflow-hidden border border-gray-800 ${className}`}>
      <div className="flex items-center justify-between px-4 py-2 bg-gray-950 border-b border-gray-800">
        <span className="text-xs text-gray-500 font-mono">{language}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 transition-colors"
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="bg-gray-900 text-gray-100 text-sm leading-relaxed p-5 overflow-x-auto font-mono whitespace-pre">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function Section({ id, title, badge, children }) {
  return (
    <section id={id} className="mb-16 scroll-mt-24">
      <div className="flex items-center gap-3 mb-5">
        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        {badge && (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">
            {badge}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}

function EndpointBlock({ method, path, description, requestBody, responseBody, requestLang = "json", responseLang = "json" }) {
  const methodColors = {
    GET: "bg-blue-50 text-blue-700 border-blue-200",
    POST: "bg-green-50 text-green-700 border-green-200",
    PUT: "bg-amber-50 text-amber-700 border-amber-200",
    DELETE: "bg-red-50 text-red-700 border-red-200",
  };

  return (
    <div className="mb-8 border border-gray-100 rounded-xl overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 bg-gray-50 border-b border-gray-100">
        <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded border ${methodColors[method] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
          {method}
        </span>
        <code className="text-sm font-mono text-gray-800">{path}</code>
      </div>
      <div className="px-5 py-4">
        <p className="text-sm text-gray-600 mb-4">{description}</p>
        {requestBody && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Request Body</p>
            <CodeBlock code={requestBody} language={requestLang} />
          </div>
        )}
        {responseBody && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Response</p>
            <CodeBlock code={responseBody} language={responseLang} />
          </div>
        )}
      </div>
    </div>
  );
}

function CollapsibleModel({ title, json }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 rounded-xl mb-4 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-semibold text-gray-900 font-mono">{title}</span>
        <ChevronRight
          size={15}
          className={`text-gray-400 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>
      {open && <CodeBlock code={json} language="json" className="rounded-none border-0 border-t border-gray-100" />}
    </div>
  );
}

export default function ApiDocsPage() {
  const [activeSection, setActiveSection] = useState("overview");
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      { rootMargin: "-30% 0px -60% 0px" }
    );

    NAV_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-3 h-3 bg-black rounded-full" />
            <span className="text-lg font-semibold tracking-tight">
              <span className="text-gray-800">SHADOW</span>
              <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
                LABS
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-5">
            <Link to="/platform" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
              Platform
            </Link>
            <Link
              to="/platform#waitlist"
              onClick={(e) => { e.preventDefault(); window.location.href = "/platform#waitlist"; }}
              className="text-sm font-medium bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Join Waitlist
            </Link>
          </div>
        </nav>
      </header>

      <div className="pt-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-10">
        {/* Left nav — sticky */}
        <aside className="hidden lg:block w-52 flex-shrink-0 pt-10 sticky top-16 self-start h-[calc(100vh-4rem)] overflow-y-auto pb-10">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4 px-3">
            Contents
          </p>
          <nav className="space-y-0.5">
            {NAV_SECTIONS.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`w-full text-left text-sm px-3 py-2 rounded-lg transition-colors ${
                  activeSection === id
                    ? "bg-teal-50 text-teal-700 font-medium"
                    : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
          <div className="mt-8 px-3">
            <Link
              to="/platform"
              className="text-xs text-teal-600 hover:text-teal-700 font-medium inline-flex items-center gap-1 transition-colors"
            >
              <ArrowRight size={11} className="rotate-180" /> Platform overview
            </Link>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 pt-10 pb-20 min-w-0">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <p className="text-xs tracking-widest uppercase text-teal-600 font-medium mb-2">
              Platform API
            </p>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight mb-2">
              API Reference
            </h1>
            <p className="text-gray-500 text-base mb-10 max-w-2xl">
              Everything you need to configure and manage your tenant's conversation intelligence capabilities.
            </p>
          </motion.div>

          {/* Overview */}
          <Section id="overview" title="Overview">
            <p className="text-sm text-gray-600 leading-relaxed mb-4">
              The Shadow Platform API lets you embed real-time conversation intelligence into any product.
              You define which capabilities run for your tenant, override prompts for your vertical,
              create custom capability types, and subscribe to the resulting signal stream.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed mb-4">
              All management endpoints are available now. Real-time stream primitives (
              <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono">stream.connect</code>,{" "}
              <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono">signals.subscribe</code>, etc.)
              are in development — join the{" "}
              <Link to="/platform" className="text-teal-600 hover:underline">waitlist</Link> to be notified.
            </p>
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Base URL</p>
              <CodeBlock code="https://api.shadowlabs.ai/api/platform" language="text" />
            </div>
          </Section>

          {/* Authentication */}
          <Section id="authentication" title="Authentication">
            <p className="text-sm text-gray-600 leading-relaxed mb-4">
              Every request requires two headers: your tenant identifier and a bearer API key.
              API keys are issued during onboarding.
            </p>
            <CodeBlock
              language="http"
              code={`X-Tenant-ID: your-tenant-id
Authorization: Bearer sk_live_xxxxxxxxxxxx`}
            />
            <div className="mt-4 p-4 bg-amber-50 border border-amber-100 rounded-xl">
              <p className="text-xs text-amber-800 leading-relaxed">
                <strong>Tenant isolation:</strong> Every request is scoped to the tenant in{" "}
                <code className="font-mono">X-Tenant-ID</code>. Data from one tenant is never
                accessible to another. Your <code className="font-mono">tenant_id</code> is assigned
                at onboarding and cannot be changed.
              </p>
            </div>
          </Section>

          {/* Tenant Configuration */}
          <Section id="tenant-config" title="Tenant Configuration">
            <p className="text-sm text-gray-600 leading-relaxed mb-6">
              Tenant configuration controls which platform capabilities are active, what thresholds
              they run at, and whether any capability prompts are overridden for your vertical.
            </p>

            <EndpointBlock
              method="GET"
              path="/platform/tenants/{tenant_id}/config"
              description="Retrieve the current configuration for your tenant. Returns platform defaults if no configuration has been set."
              responseBody={`{
  "tenant_id": "acme-health",
  "enabled_capabilities": ["fact_check", "follow_up"],
  "enabled_moment_types": ["factual_claim", "question_opportunity"],
  "thresholds": {
    "default_confidence": 0.5,
    "preferred_confidence": 0.35,
    "context_window": 30
  },
  "prompt_overrides": {},
  "source_routing": { "enabled": true, "confidence_threshold": 0.5 },
  "output_format": "cards",
  "webhook_url": null,
  "updated_at": "2026-03-21T10:00:00Z"
}`}
            />

            <EndpointBlock
              method="PUT"
              path="/platform/tenants/{tenant_id}/config"
              description="Create or update tenant configuration. Only the fields you provide are updated — omitted fields retain their current values."
              requestBody={`{
  "enabled_capabilities": ["fact_check", "follow_up", "clinical_flag"],
  "thresholds": {
    "default_confidence": 0.5
  }
}`}
              responseBody={`{
  "tenant_id": "acme-health",
  "enabled_capabilities": ["fact_check", "follow_up", "clinical_flag"],
  "thresholds": { "default_confidence": 0.5, ... },
  "updated_at": "2026-03-21T10:01:00Z"
}`}
            />
          </Section>

          {/* Capability Management */}
          <Section id="capabilities" title="Capability Management">
            <p className="text-sm text-gray-600 leading-relaxed mb-6">
              Capabilities are the intelligence modules that run against conversation moments.
              Platform defaults (fact_check, follow_up, argument_coach, objection_handler,
              example_generator) are always available. You can enable/disable them per tenant
              and define entirely new custom capability types.
            </p>

            <EndpointBlock
              method="GET"
              path="/platform/tenants/{tenant_id}/capabilities"
              description="List all capabilities available to this tenant — platform defaults with their enabled status, plus any custom capabilities defined for this tenant."
              responseBody={`{
  "platform_defaults": [
    { "name": "fact_check", "enabled": true, "trigger_moment_types": ["factual_claim"] },
    { "name": "follow_up", "enabled": true, "trigger_moment_types": ["question_opportunity"] },
    { "name": "argument_coach", "enabled": false, "trigger_moment_types": ["challenge"] },
    { "name": "objection_handler", "enabled": true, "trigger_moment_types": ["negotiation_tension"] },
    { "name": "example_generator", "enabled": false, "trigger_moment_types": ["explanation_needed"] }
  ],
  "custom": [
    { "capability_id": "clinical_flag", "enabled": true, "trigger_moment_types": ["factual_claim"] }
  ]
}`}
            />

            <EndpointBlock
              method="POST"
              path="/platform/tenants/{tenant_id}/capabilities"
              description="Define a new custom capability type. Provide a prompt, the moment types that trigger it, and the expected output schema for the generated card."
              requestBody={`{
  "capability_id": "clinical_flag",
  "capability_name": "Clinical Accuracy Flag",
  "trigger_moment_types": ["factual_claim"],
  "system_instruction": "You are a clinical accuracy checker. Identify medically incorrect or misleading statements in the transcript.",
  "prompt_template": "Meeting context: {meeting_goal}\\n\\nTranscript:\\n{transcript_text}\\n\\nTrigger: {trigger}\\n\\nReturn JSON: { claim, verdict, clinical_note, urgency }",
  "model": "gemini-2.5-flash",
  "thinking_budget": 0,
  "output_schema": {
    "claim": "string",
    "verdict": "string",
    "clinical_note": "string",
    "urgency": "string"
  },
  "urgency": "immediate",
  "enabled": true
}`}
              responseBody={`{
  "capability_id": "clinical_flag",
  "tenant_id": "acme-health",
  "enabled": true,
  "created_at": "2026-03-21T10:02:00Z"
}`}
            />

            <EndpointBlock
              method="PUT"
              path="/platform/tenants/{tenant_id}/capabilities/{capability_id}"
              description="Update an existing custom capability — enable/disable it, change its prompt, or update its trigger moment types."
              requestBody={`{
  "enabled": false
}`}
              responseBody={`{
  "capability_id": "clinical_flag",
  "enabled": false,
  "updated_at": "2026-03-21T10:03:00Z"
}`}
            />

            <EndpointBlock
              method="DELETE"
              path="/platform/tenants/{tenant_id}/capabilities/{capability_id}"
              description="Permanently delete a custom capability. Platform default capabilities cannot be deleted — only disabled."
              responseBody={`{
  "status": "deleted",
  "capability_id": "clinical_flag"
}`}
            />
          </Section>

          {/* Prompt Overrides */}
          <Section id="prompt-overrides" title="Prompt Overrides">
            <p className="text-sm text-gray-600 leading-relaxed mb-4">
              Platform default capabilities run with Shadow's built-in prompts. You can override
              the system instruction and/or prompt template for any platform capability to adapt
              it to your vertical's language and requirements.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed mb-6">
              Overrides are set as part of your tenant configuration under{" "}
              <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono">prompt_overrides</code>.
            </p>
            <CodeBlock
              language="json — PUT /platform/tenants/{tenant_id}/config"
              code={`{
  "prompt_overrides": {
    "fact_check": {
      "system_instruction": "You are a clinical accuracy checker for a healthcare platform. Identify medically incorrect statements, dosage errors, and misleading clinical claims.",
      "model": "gemini-2.5-flash",
      "thinking_budget": 1024
    }
  }
}`}
            />
            <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-xl">
              <p className="text-xs text-blue-800 leading-relaxed">
                <strong>Partial overrides:</strong> You only need to provide the fields you want to change.
                Omitted fields (e.g. <code className="font-mono">prompt_template</code>) fall back to
                the platform default. Shadow's own prompts are never modified — overrides apply to your
                tenant only.
              </p>
            </div>
          </Section>

          {/* Test Endpoint */}
          <Section id="test-endpoint" title="Test Endpoint">
            <p className="text-sm text-gray-600 leading-relaxed mb-6">
              Test any capability — platform default or custom — against a sample transcript
              without persisting anything. Useful for validating prompts and output schemas
              before going live.
            </p>

            <EndpointBlock
              method="POST"
              path="/platform/tenants/{tenant_id}/capabilities/{capability_id}/test"
              description="Run a capability against a sample transcript and return the generated card. Nothing is stored. The full tenant config and prompt overrides are applied."
              requestBody={`{
  "sample_transcript": [
    { "speaker": "Doctor", "text": "The standard dose of metformin is 2000mg daily." },
    { "speaker": "Patient", "text": "I thought it was 1000mg twice a day?" }
  ],
  "context_hint": "Clinical consultation about diabetes medication"
}`}
              responseBody={`{
  "card_type": "clinical_flag",
  "urgency": "immediate",
  "content": {
    "claim": "Standard dose of metformin is 2000mg daily",
    "verdict": "Partially correct",
    "clinical_note": "Standard starting dose is 500mg twice daily. Max is 2550mg/day. '2000mg daily' is within range but the framing is imprecise.",
    "urgency": "when_ready"
  },
  "agent": "generic:clinical_flag",
  "tenant_id": "acme-health",
  "persisted": false
}`}
            />
          </Section>

          {/* API Reference — Coming Soon */}
          <Section id="api-reference" title="API Reference" badge="Coming Soon">
            <div className="p-5 bg-gray-50 border border-gray-100 rounded-xl mb-4">
              <p className="text-sm text-gray-500 leading-relaxed">
                Real-time stream primitives are in active development. These endpoints will enable
                connecting live audio streams, subscribing to real-time signal events, and
                triggering downstream actions.
              </p>
              <Link
                to="/platform"
                className="inline-flex items-center gap-1.5 mt-4 text-sm text-teal-600 hover:text-teal-700 font-medium transition-colors"
              >
                Join the waitlist to get notified <ArrowRight size={13} />
              </Link>
            </div>
            <div className="space-y-3 opacity-50 pointer-events-none select-none">
              {[
                { method: "POST", path: "/platform/stream/connect", desc: "Connect a real-time audio stream or transcript feed" },
                { method: "POST", path: "/platform/intelligence/configure", desc: "Configure intelligence capabilities for a session" },
                { method: "GET", path: "/platform/signals/subscribe", desc: "Subscribe to real-time signal stream via SSE" },
                { method: "GET", path: "/platform/memory/query", desc: "Query conversation graph for a person, deal, or context" },
                { method: "POST", path: "/platform/actions/trigger", desc: "Trigger downstream actions from a signal" },
                { method: "GET", path: "/platform/insights/export", desc: "Export structured conversation data and signal history" },
              ].map((ep) => {
                const methodColors = {
                  GET: "bg-blue-50 text-blue-700 border-blue-200",
                  POST: "bg-green-50 text-green-700 border-green-200",
                };
                return (
                  <div key={ep.path} className="flex items-center gap-3 border border-gray-100 rounded-lg px-4 py-3">
                    <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded border ${methodColors[ep.method]}`}>
                      {ep.method}
                    </span>
                    <code className="text-sm font-mono text-gray-700">{ep.path}</code>
                    <span className="text-sm text-gray-400 ml-auto">{ep.desc}</span>
                  </div>
                );
              })}
            </div>
          </Section>

          {/* Data Models */}
          <Section id="data-models" title="Data Models">
            <p className="text-sm text-gray-600 leading-relaxed mb-6">
              Full shapes for the core data structures used across the API.
            </p>
            <CollapsibleModel
              title="tenant_configs"
              json={`{
  "tenant_id": "string — unique identifier assigned at onboarding",
  "enabled_capabilities": [
    "string — capability names: fact_check | follow_up | argument_coach | objection_handler | example_generator | <custom_id>"
  ],
  "enabled_moment_types": [
    "string — factual_claim | challenge | explanation_needed | question_opportunity | negotiation_tension | <custom_type>"
  ],
  "thresholds": {
    "default_confidence": "float — 0.0–1.0, default 0.45",
    "preferred_confidence": "float — 0.0–1.0, default 0.35",
    "context_window": "int — transcript segments in context, default 30",
    "classifier_window": "int — segments sent to classifier, default 15",
    "content_dedup_window_seconds": "int — dedup lookback window, default 120",
    "content_dedup_overlap_threshold": "float — 0.0–1.0, default 0.65"
  },
  "prompt_overrides": {
    "<capability_name>": {
      "system_instruction": "string — optional override",
      "prompt_template": "string — optional override, use {transcript_text}, {meeting_goal}, {trigger} as variables",
      "model": "string — default gemini-2.5-flash",
      "thinking_budget": "int — default 0"
    }
  },
  "source_routing": {
    "enabled": "bool — default true",
    "confidence_threshold": "float — default 0.5"
  },
  "output_format": "string — cards | webhook",
  "webhook_url": "string | null — required if output_format is webhook",
  "created_at": "ISO 8601 datetime",
  "updated_at": "ISO 8601 datetime"
}`}
            />
            <CollapsibleModel
              title="tenant_capabilities"
              json={`{
  "tenant_id": "string",
  "capability_id": "string — unique within tenant, e.g. clinical_flag",
  "capability_name": "string — display name",
  "trigger_moment_types": [
    "string — which moment types cause this capability to run"
  ],
  "system_instruction": "string — LLM system prompt for this capability",
  "prompt_template": "string — prompt body with {transcript_text}, {meeting_goal}, {trigger} variables",
  "model": "string — default gemini-2.5-flash",
  "thinking_budget": "int — 0 disables thinking mode",
  "output_schema": {
    "<field>": "string — expected type description"
  },
  "urgency": "string — immediate | when_ready | background",
  "enabled": "bool",
  "created_at": "ISO 8601 datetime"
}`}
            />
            <CollapsibleModel
              title="signal card (SmartCard)"
              json={`{
  "card_id": "string — card_<12 hex chars>",
  "card_type": "string — fact_check | follow_up | defense | example | objection | grounded | <custom_capability_id>",
  "urgency": "string — immediate | when_ready | background",
  "content": "object — shape depends on capability; see output_schema",
  "agent": "string — agent identifier, e.g. FactSurfacerAgent or generic:clinical_flag",
  "tenant_id": "string",
  "target_user_id": "string | null",
  "timestamp": "ISO 8601 datetime"
}`}
            />
          </Section>
        </main>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 text-center mt-8">
        <p className="text-sm text-gray-400">
          © 2026 ShadowLabs{" "}
          <span className="mx-2 text-gray-200">·</span>
          <Link to="/privacy" className="hover:text-gray-600 transition-colors">Privacy</Link>
          <span className="mx-2 text-gray-200">·</span>
          <Link to="/terms" className="hover:text-gray-600 transition-colors">Terms</Link>
          <span className="mx-2 text-gray-200">·</span>
          <Link to="/platform" className="hover:text-gray-600 transition-colors">Platform</Link>
          <span className="mx-2 text-gray-200">·</span>
          <Link to="/" className="hover:text-gray-600 transition-colors">Back to app</Link>
        </p>
      </footer>
    </div>
  );
}
